import type { CharterSeatConfigurationOverrideUpdatePayload } from '~/services/CharterService';

import type { CharterCabinClass, SeatInfo } from '../charter.types';

export const SEAT_OVERRIDE_DEPARTING_DEP_AIRPORT = 'MEL';
export const SEAT_OVERRIDE_DEPARTING_ARR_AIRPORT = 'MLE';
export const SEAT_OVERRIDE_RETURNING_DEP_AIRPORT = 'MLE';
export const SEAT_OVERRIDE_RETURNING_ARR_AIRPORT = 'MEL';

export const CABIN_LABELS: Record<CharterCabinClass, string> = {
  business: 'Business',
  economy: 'Economy',
  lux_row: 'Economy Extra',
  lux_row_pax_one: 'Economy Extra for One',
  premium_economy: 'Premium Economy',
};

export const MANUAL_BLOCK_REASON_LABELS = {
  crew_rest: 'Crew rest',
  vvip: 'VVIP',
  lux_row: 'Economy Extra',
  dummy: 'Dummy block',
} as const;

export type ManualBlockReason = keyof typeof MANUAL_BLOCK_REASON_LABELS;

export type SeatOverrideFormValue = {
  isAvailable: boolean;
  blockReason?: ManualBlockReason;
  price: number;
};

export type BulkSeatOverrideFormValue = {
  applyAvailability: boolean;
  isAvailable: boolean;
  blockReason?: ManualBlockReason;
  applyPrice: boolean;
  price: number;
};

export type BulkSeatOverrideResetValue = {
  resetToBase: true;
};

export function getSeatCanonicalId(seat: SeatInfo): string {
  return seat.primaryColumn ?? seat.seatId;
}

export function isSeatSelectableForBulk(seat: SeatInfo): boolean {
  return !seat.isOccupied;
}

export function getBulkSeatOverrideFormValidation(value: BulkSeatOverrideFormValue): {
  hasSelectedField: boolean;
  blockReasonError: boolean;
  priceError: boolean;
  isValid: boolean;
} {
  const hasSelectedField = value.applyAvailability || value.applyPrice;
  const blockReasonError = value.applyAvailability && !value.isAvailable && !value.blockReason;
  const priceError = value.applyPrice && (!Number.isFinite(value.price) || value.price < 0);

  return {
    hasSelectedField,
    blockReasonError,
    priceError,
    isValid: hasSelectedField && !blockReasonError && !priceError,
  };
}

export function buildBulkSeatConfigurationOverridePayload(
  seats: Array<SeatInfo>,
  value: BulkSeatOverrideFormValue,
): CharterSeatConfigurationOverrideUpdatePayload {
  return {
    seats: seats.filter(isSeatSelectableForBulk).map((seat) => {
      const isAvailable = value.applyAvailability ? value.isAvailable : seat.isAvailable;
      const blockReason = value.applyAvailability ? value.blockReason : seat.blockReason;

      return {
        cabin: seat.cabin,
        seatId: getSeatCanonicalId(seat),
        price: value.applyPrice ? value.price : (seat.price ?? 0),
        isAvailable,
        blockReason: isAvailable || blockReason === 'occupied' ? undefined : blockReason,
      };
    }),
  };
}

export function buildResetToBaseSeatConfigurationOverridePayload(
  seats: Array<SeatInfo>,
): CharterSeatConfigurationOverrideUpdatePayload {
  return {
    seats: seats.filter(isSeatSelectableForBulk).map((seat) => {
      const isAvailable = seat.baseIsAvailable ?? seat.isAvailable;
      const blockReason = seat.baseBlockReason;

      return {
        cabin: seat.cabin,
        seatId: getSeatCanonicalId(seat),
        price: seat.basePrice ?? seat.price ?? 0,
        isAvailable,
        blockReason: isAvailable || blockReason === 'occupied' ? undefined : blockReason,
      };
    }),
  };
}
